import { useRef, useEffect, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Bot, Paperclip, RefreshCw, User } from 'lucide-react';
import {
  InquiryVersioningChoice,
  LlmChatMessageDto,
  LlmChatUiActionType,
  type LlmChatMessageUiAction,
} from '@paladin-monorepo/common';
import { getVersioningChoiceDisplayLabel } from './LlmChatInquiryVersioningActions';
import { getAgentQuestionDisplayLabel } from './LlmChatAgentQuestionActions';
import { LlmChatMessageBubble } from './LlmChatMessageBubble';
import { LlmChatToolCallsSummary } from './LlmChatToolCallsSummary';
import { MarkdownContent } from './MarkdownContent';
import { StreamingIndicator } from './StreamingIndicator';
import { groupLlmChatMessagesForDisplay } from './llm-chat-message-display';
import { Button } from '@/components/ui/button';
import type { StreamingToolEvent } from '@/hooks/useLlmChatStream';

function getPendingUserMessageDisplay(
  pendingUserMessage: string,
  messages: LlmChatMessageDto[],
  streamingUiAction: LlmChatMessageUiAction | null | undefined,
  translate: (key: string) => string,
): string {
  if (
    pendingUserMessage === InquiryVersioningChoice.UpdateCurrent ||
    pendingUserMessage === InquiryVersioningChoice.CreateNew
  ) {
    return getVersioningChoiceDisplayLabel(pendingUserMessage, translate);
  }

  const agentQuestionUiAction =
    streamingUiAction?.type === LlmChatUiActionType.AgentQuestion
      ? streamingUiAction
      : [...messages]
          .reverse()
          .find((message) => message.uiAction?.type === LlmChatUiActionType.AgentQuestion)?.uiAction;

  if (agentQuestionUiAction?.type === LlmChatUiActionType.AgentQuestion) {
    return getAgentQuestionDisplayLabel(pendingUserMessage, agentQuestionUiAction);
  }

  return pendingUserMessage;
}

interface LlmChatMessageListProps {
  messages: LlmChatMessageDto[];
  streamingContent: string;
  streamingUiAction?: LlmChatMessageUiAction | null;
  streamingTools?: StreamingToolEvent[];
  isStreaming: boolean;
  pendingUserMessage?: string | null;
  pendingAttachmentNames?: string[];
  showAssistantRetry?: boolean;
  onAssistantRetry?: () => void;
  onSend?: (content: string) => void;
  afterMessages?: ReactNode;
}

export function LlmChatMessageList({
  messages,
  streamingContent,
  streamingUiAction,
  streamingTools,
  isStreaming,
  pendingUserMessage,
  pendingAttachmentNames,
  showAssistantRetry,
  onAssistantRetry,
  onSend,
  afterMessages,
}: LlmChatMessageListProps) {
  const { t } = useTranslation();
  const listRef = useRef<HTMLDivElement>(null);

  const hasAfterMessages = afterMessages != null;

  useEffect(() => {
    const list = listRef.current;
    if (!list) return;
    // Instant scroll: a smooth one restarts on every streamed chunk and lags behind the text.
    list.scrollTo({ top: list.scrollHeight, behavior: streamingContent ? 'auto' : 'smooth' });
  }, [messages.length, streamingContent, streamingUiAction, streamingTools, pendingUserMessage, showAssistantRetry, hasAfterMessages]);

  return (
    <div ref={listRef} className="min-h-0 flex-1 overflow-y-auto p-6">
      <div className="mx-auto max-w-3xl space-y-4">
        {groupLlmChatMessagesForDisplay(messages).map((item) => {
          if (item.type === 'toolSequence') {
            return (
              <div key={item.id} className="flex gap-3">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted">
                  <Bot className="h-4 w-4" />
                </div>
                <div className="min-w-0 max-w-[75%] rounded-2xl bg-muted px-4 py-2">
                  <LlmChatToolCallsSummary toolCalls={item.toolCalls} />
                </div>
              </div>
            );
          }

          return (
            <LlmChatMessageBubble
              key={item.message.id}
              message={item.message}
              allMessages={messages}
              onSend={onSend}
              actionsDisabled={isStreaming}
            />
          );
        })}

        {pendingUserMessage && (
          <div className="flex gap-3 flex-row-reverse">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground">
              <User className="h-4 w-4" />
            </div>
            <div className="max-w-[75%] rounded-2xl px-4 py-2.5 bg-primary text-primary-foreground">
              <MarkdownContent
                content={getPendingUserMessageDisplay(
                  pendingUserMessage,
                  messages,
                  streamingUiAction,
                  t,
                )}
              />
              {pendingAttachmentNames && pendingAttachmentNames.length > 0 && (
                <div className="mt-2 space-y-1 opacity-80">
                  {pendingAttachmentNames.map((name, i) => (
                    <div key={`${name}-${i}`} className="flex max-w-full items-center gap-1.5 text-xs" title={name}>
                      <Paperclip className="h-3 w-3 shrink-0" />
                      <span className="truncate">{name}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {showAssistantRetry && onAssistantRetry && (
          <div className="flex gap-3">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted">
              <Bot className="h-4 w-4" />
            </div>
            <div className="flex max-w-[75%] flex-col gap-3 rounded-2xl bg-muted px-4 py-2.5">
              <p className="text-sm text-muted-foreground">{t('llmChat.assistantReplyMissing')}</p>
              <Button type="button" variant="secondary" size="sm" className="w-fit" onClick={onAssistantRetry}>
                <RefreshCw className="mr-2 h-4 w-4" />
                {t('llmChat.retryAssistantReply')}
              </Button>
            </div>
          </div>
        )}

        {isStreaming && (
          <StreamingIndicator
            content={streamingContent}
            uiAction={streamingUiAction}
            tools={streamingTools}
            onUiActionSend={onSend}
            onVersioningSelect={
              onSend
                ? (choice) => onSend(choice)
                : undefined
            }
          />
        )}
      </div>
      {afterMessages && (
        <div className="mx-auto mt-6 max-w-5xl">
          {afterMessages}
        </div>
      )}
    </div>
  );
}
